/** Single reaction option shown in the picker and floating overlays. */
export type CommonSenseReaction = {
	id: string
	/** Unicode emoji (default set). */
	emoji?: string
	/** Optional custom image URL for file-based sets. */
	image?: string
	label?: string
}

/** Fixed number of reaction slots per event. */
export const COMMON_SENSE_REACTION_COUNT = 8

/** @deprecated Use COMMON_SENSE_REACTION_COUNT */
export const COMMON_SENSE_MAX_REACTIONS = COMMON_SENSE_REACTION_COUNT

/** Default picker: eight emoji slots (4×2 grid). */
export const COMMON_SENSE_DEFAULT_REACTIONS: CommonSenseReaction[] = [
	{ id: "reaction_1", emoji: "👍", label: "Reaction 1" },
	{ id: "reaction_2", emoji: "❤️", label: "Reaction 2" },
	{ id: "reaction_3", emoji: "😂", label: "Reaction 3" },
	{ id: "reaction_4", emoji: "🔥", label: "Reaction 4" },
	{ id: "reaction_5", emoji: "🎉", label: "Reaction 5" },
	{ id: "reaction_6", emoji: "👏", label: "Reaction 6" },
	{ id: "reaction_7", emoji: "😮", label: "Reaction 7" },
	{ id: "reaction_8", emoji: "💯", label: "Reaction 8" },
]

/**
 * File-based reaction sets.
 * Add new sets here (for example with `/src/assets/common_sense/reactions/<set>/reaction_X.png`)
 * and wire a dropdown later to switch by id.
 */
export const COMMON_SENSE_REACTION_SETS: Record<string, CommonSenseReaction[]> = {
	default: [...COMMON_SENSE_DEFAULT_REACTIONS],
}

export const DEFAULT_COMMON_SENSE_REACTION_SET_ID = "default"

/**
 * Legacy/array override: merges up to eight explicit entries onto the default slots by index.
 */
export function resolveCommonSenseReactions(custom?: CommonSenseReaction[] | null): CommonSenseReaction[] {
	if (!custom?.length) {
		return [...COMMON_SENSE_DEFAULT_REACTIONS]
	}
	return COMMON_SENSE_DEFAULT_REACTIONS.map((slotDefault, index) => {
		const override = custom[index]
		if (!override) return { ...slotDefault }
		return {
			id: override.id || slotDefault.id,
			emoji: override.image ? undefined : override.emoji ?? slotDefault.emoji,
			image: override.image?.trim() || undefined,
			label: override.label ?? slotDefault.label,
		}
	})
}

export function resolveReactionSetById(setId?: string | null): CommonSenseReaction[] {
	const id = (setId ?? DEFAULT_COMMON_SENSE_REACTION_SET_ID).trim()
	const fromSet = COMMON_SENSE_REACTION_SETS[id]
	if (!fromSet) return [...COMMON_SENSE_DEFAULT_REACTIONS]
	return resolveCommonSenseReactions(fromSet)
}

export function reactionDisplaySrc(reaction: CommonSenseReaction | undefined): string | null {
	if (!reaction) return null
	if (reaction.image?.trim()) return reaction.image.trim()
	return null
}

export function reactionDisplayEmoji(reaction: CommonSenseReaction | undefined): string {
	if (!reaction) return ""
	return reaction.emoji?.trim() || "✨"
}
